import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminHealthCheckShimmer } from '../common/Shimmer.jsx';
import SortOrderToggle from '../common/SortOrderToggle.jsx';
import { IconChevronDown, IconRefresh } from '../common/AdminIcons.jsx';
import AdminHealthCheckRepository from '../../services/AdminHealthCheckRepository.js';
import { devError } from '../../utils/logger.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminHealthCheck.css';

const POLL_INTERVAL_MS = 300_000;

const STATUS_LABELS = {
  healthy: '正常',
  degraded: '警告',
  down: '異常',
  unconfigured: '未設定',
  pending: '未実行',
};

const STATUS_ORDER = {
  down: 0,
  degraded: 1,
  unconfigured: 2,
  pending: 3,
  healthy: 4,
};

const CATEGORY_LABELS = {
  platform: 'プラットフォーム',
  product: 'プロダクト',
};

const ENV_LABELS = {
  prod: '本番',
  dev: '開発',
};

const SORT_OPTIONS = [
  { value: 'displayOrder', label: '表示順' },
  { value: 'name', label: '名前' },
  { value: 'status', label: '状態' },
  { value: 'latency', label: 'レイテンシ' },
  { value: 'category', label: '種別' },
  { value: 'environment', label: '環境' },
];

function formatCheckedAt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

function formatLatency(ms) {
  if (ms == null) return '—';
  return `${ms} ms`;
}

function formatHttpStatus(code) {
  if (code == null) return '—';
  return String(code);
}

function shortenUrl(url) {
  if (!url) return 'URL 未設定';
  try {
    const normalized = url.startsWith('http') ? url : `https://${url}`;
    const u = new URL(normalized);
    const path = u.pathname && u.pathname !== '/' ? u.pathname : '';
    const host = u.host + path;
    return host.length > 56 ? `${host.slice(0, 53)}…` : host;
  } catch {
    return url.length > 56 ? `${url.slice(0, 53)}…` : url;
  }
}

const FilterButton = ({ active, onClick, children }) => (
  <button type="button" className={`health-filter-btn${active ? ' active' : ''}`} onClick={onClick}>
    {children}
  </button>
);

const FilterGroup = ({ label, children }) => (
  <div className="health-filter-group">
    <span className="health-filter-group-label">{label}</span>
    <div className="health-filter-group-buttons">{children}</div>
  </div>
);

const ComponentSection = ({ title, layer }) => {
  if (!layer?.components?.length) return null;
  return (
    <>
      <div className="health-attempts-title">{title}</div>
      <div className="health-components-list">
        {layer.components.map((comp) => (
          <div key={comp.id} className="health-component-item">
            <span className={`health-status-badge ${comp.status}`}>
              {STATUS_LABELS[comp.status] || comp.status}
            </span>
            <span className="health-component-id">{comp.id}</span>
            {comp.critical && <span className="health-component-critical">必須</span>}
            <span className="health-metric">{formatLatency(comp.latencyMs)}</span>
            {comp.message && <span className="health-component-message">{comp.message}</span>}
          </div>
        ))}
      </div>
    </>
  );
};

const HealthCheckDetail = ({ item }) => (
  <div className="health-check-detail">
    <div className="health-detail-grid">
      <div className="health-detail-item">
        <div className="label">起動 URL（設定値）</div>
        <div className="value mono">{item.sourceUrl || '—'}</div>
      </div>
      <div className="health-detail-item">
        <div className="label">L1 チェック URL</div>
        <div className="value mono">{item.liveness?.checkedUrl || item.checkedUrl || '—'}</div>
      </div>
      <div className="health-detail-item">
        <div className="label">L2 確認</div>
        <div className="value">{formatCheckedAt(item.detailed?.checkedAt)}</div>
      </div>
      <div className="health-detail-item">
        <div className="label">L3 確認</div>
        <div className="value">{formatCheckedAt(item.smoke?.checkedAt)}</div>
      </div>
      <div className="health-detail-item">
        <div className="label">エラー</div>
        <div className="value">{item.error || '—'}</div>
      </div>
      <div className="health-detail-item">
        <div className="label">最終確認</div>
        <div className="value">{formatCheckedAt(item.checkedAt)}</div>
      </div>
    </div>

    {Array.isArray(item.attempts) && item.attempts.length > 0 && (
      <>
        <div className="health-attempts-title">L1 試行したエンドポイント</div>
        <div className="health-attempts-list">
          {item.attempts.map((attempt) => (
            <div key={attempt.url} className="health-attempt-item">
              <span className={`health-status-badge ${attempt.status}`}>{STATUS_LABELS[attempt.status] || attempt.status}</span>
              <span className="url">{attempt.url}</span>
              <span className="health-metric">{formatHttpStatus(attempt.httpStatus)}</span>
              <span className="health-metric">{formatLatency(attempt.latencyMs)}</span>
              {attempt.error && <span className="health-metric">{attempt.error}</span>}
            </div>
          ))}
        </div>
      </>
    )}

    <ComponentSection title="L2 コンポーネント（インフラ）" layer={item.detailed} />
    <ComponentSection title="L3 コンポーネント（AI スモーク）" layer={item.smoke} />

    {item.bodyPreview && (
      <>
        <div className="health-body-title">レスポンス（プレビュー）</div>
        <pre className="health-body-preview">{item.bodyPreview}</pre>
      </>
    )}
  </div>
);

function mergeProbeIntoResult(prev, l2Result, l3Result) {
  const liveness = l2Result?.liveness || l3Result?.liveness || prev?.liveness;
  const detailed = l2Result?.detailed || prev?.detailed;
  const smoke = l3Result?.smoke || prev?.smoke;
  const statusOrder = STATUS_ORDER;
  let status = l2Result?.status || l3Result?.status || prev?.status || 'pending';
  if (l2Result?.status) status = statusOrder[l2Result.status] < statusOrder[status] ? l2Result.status : status;
  if (l3Result?.status) status = statusOrder[l3Result.status] < statusOrder[status] ? l3Result.status : status;

  return {
    ...(l2Result || l3Result || prev),
    status,
    liveness,
    detailed,
    smoke,
    checkedAt: [l2Result?.checkedAt, l3Result?.checkedAt, prev?.checkedAt].filter(Boolean).sort().pop(),
    attempts: liveness?.attempts || l2Result?.attempts || prev?.attempts || [],
    latencyMs: liveness?.latencyMs ?? l2Result?.latencyMs ?? prev?.latencyMs,
    httpStatus: liveness?.httpStatus ?? l2Result?.httpStatus ?? prev?.httpStatus,
    checkedUrl: liveness?.checkedUrl ?? l2Result?.checkedUrl ?? prev?.checkedUrl,
    bodyPreview: detailed?.bodyPreview || smoke?.bodyPreview || prev?.bodyPreview,
    error: l2Result?.error || l3Result?.error || prev?.error,
  };
}

const AdminHealthCheck = () => {
  const probe = useScreenProbe('health-check');
  const repo = AdminHealthCheckRepository.getInstance();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningFull, setRunningFull] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [probingId, setProbingId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [envFilter, setEnvFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [sortKey, setSortKey] = useState('displayOrder');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showRunModal, setShowRunModal] = useState(true);
  const pollRef = useRef(null);

  const loadReport = useCallback(async (options = {}) => {
    const silent = options.silent ?? false;
    if (!silent) {
      probe.start('load');
      setLoading(true);
    }
    setError('');
    try {
      const data = await repo.fetchReport();
      setReport(data);
      if (!silent) {
        probe.end('load', 'ok');
        probe.unlock();
      }
    } catch (err) {
      devError('ヘルスチェック取得エラー:', err);
      if (!silent) {
        probe.end('load', `error: ${err?.message || 'unknown'}`);
        setError(err.message || 'ヘルスチェックの取得に失敗しました');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [repo, probe]);

  const runFullCheck = useCallback(async (options = {}) => {
    const silent = options.silent ?? false;
    if (!silent) {
      setRunningFull(true);
      setLoading(true);
    }
    setError('');
    try {
      const data = await repo.runFullCheck();
      setReport(data);
    } catch (err) {
      devError('ヘルスチェック全件実行エラー:', err);
      if (!silent) {
        setError(err.message || '全件チェックに失敗しました');
      }
    } finally {
      if (!silent) {
        setRunningFull(false);
        setLoading(false);
      }
    }
  }, [repo]);

  useEffect(() => {
    loadReport();
    pollRef.current = setInterval(() => {
      loadReport({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadReport]);

  const handleConfirmRunModal = async () => {
    setShowRunModal(false);
    await runFullCheck();
  };

  const handleCancelRunModal = () => {
    setShowRunModal(false);
  };

  const handleRunFullCheck = () => runFullCheck();

  const toggleRowExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleProbeOne = async (probeId) => {
    setProbingId(probeId);
    setError('');
    try {
      const l2Result = await repo.probeTarget(probeId, 'l2');
      const l3Result = await repo.probeTarget(probeId, 'l3');
      const merged = mergeProbeIntoResult(null, l2Result, l3Result);
      setReport((prev) => {
        if (!prev) return prev;
        const results = prev.results.map((item) => (
          item.id === probeId ? mergeProbeIntoResult(item, l2Result, l3Result) : item
        ));
        const active = results.filter((r) => r.status !== 'pending');
        const summary = {
          total: results.length,
          healthy: active.filter((r) => r.status === 'healthy').length,
          degraded: active.filter((r) => r.status === 'degraded').length,
          down: active.filter((r) => r.status === 'down').length,
          unconfigured: active.filter((r) => r.status === 'unconfigured').length,
          pending: results.filter((r) => r.status === 'pending').length,
        };
        return {
          ...prev,
          checkedAt: merged.checkedAt || prev.checkedAt,
          summary,
          results,
        };
      });
    } catch (err) {
      devError('ヘルスチェック再実行エラー:', err);
      setError(err.message || '再チェックに失敗しました');
    } finally {
      setProbingId(null);
    }
  };

  const filteredResults = useMemo(() => {
    if (!report?.results) return [];

    const filtered = report.results.filter((item) => {
      if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
      if (envFilter !== 'all' && item.environment !== envFilter) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (visibilityFilter === 'public' && item.category === 'product' && item.isActive === false) return false;
      if (visibilityFilter === 'private' && (item.category !== 'product' || item.isActive !== false)) return false;
      return true;
    });

    const direction = sortOrder === 'asc' ? 1 : -1;

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = (a.name || '').localeCompare(b.name || '', 'ja');
          break;
        case 'status':
          cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
          break;
        case 'latency':
          cmp = (a.latencyMs ?? -1) - (b.latencyMs ?? -1);
          break;
        case 'category':
          cmp = (a.category || '').localeCompare(b.category || '');
          break;
        case 'environment':
          cmp = (a.environment || '').localeCompare(b.environment || '');
          break;
        case 'displayOrder':
        default: {
          const orderA = a.displayOrder ?? (a.category === 'platform' ? -1000 + (a.id.includes('admin') ? 0 : 1) : 9999);
          const orderB = b.displayOrder ?? (b.category === 'platform' ? -1000 + (b.id.includes('admin') ? 0 : 1) : 9999);
          cmp = orderA - orderB;
          if (cmp === 0) cmp = (a.name || '').localeCompare(b.name || '', 'ja');
          break;
        }
      }
      return cmp * direction;
    });
  }, [report, categoryFilter, envFilter, statusFilter, visibilityFilter, sortKey, sortOrder]);

  const summary = report?.summary || {
    total: 0,
    healthy: 0,
    degraded: 0,
    down: 0,
    unconfigured: 0,
    pending: 0,
  };

  if (loading && !report) {
    return <AdminHealthCheckShimmer />;
  }

  return (
    <div className="admin-health-check">
      {showRunModal && (
        <div className="modal-overlay">
          <div className="modal health-check-run-modal" role="dialog" aria-labelledby="health-run-modal-title">
            <div className="modal-header">
              <h2 id="health-run-modal-title">ヘルスチェックの実行</h2>
            </div>
            <div className="modal-body">
              <p>
                L2（インフラ詳細チェック）と L3（AI スモーク）を実行します。
                外部アプリへシークレット付きでプローブを送ります。実行しますか？
              </p>
              <ul className="health-run-modal-list">
                <li>L1+L2: HTTP 生存確認と Firestore / Storage / Auth Hub 等（15分間隔）</li>
                <li>L1+L3: HTTP 生存確認と Vertex AI スモーク（30分間隔・SA 権限）</li>
              </ul>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCancelRunModal}>
                スキップ（保存済み結果のみ表示）
              </button>
              <button type="button" className="btn btn-primary" onClick={handleConfirmRunModal}>
                実行する
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="section-header">
        <div>
          <h1>ヘルスチェック</h1>
          <p>
            L2（15分ごと）: L1 生存確認 + インフラ詳細チェック。L3（30分ごと）: L1 + AI スモーク。画面を開くと実行確認が表示されます。非公開プロダクトも含みます。
            {report?.source === 'catalog-only' && ' （初回チェック待ち）'}
          </p>
        </div>
        <div className="header-actions">
          {report?.checkedAt && (
            <span className="checked-at">
              最終確認: {formatCheckedAt(report.checkedAt)}
              {report.checkedAtL2 && ` / L2: ${formatCheckedAt(report.checkedAtL2)}`}
              {report.checkedAtL3 && ` / L3: ${formatCheckedAt(report.checkedAtL3)}`}
            </span>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => loadReport()}
            disabled={loading || runningFull}
          >
            {loading ? '更新中...' : '最新を取得'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRunFullCheck}
            disabled={runningFull || !!probingId}
          >
            {runningFull ? 'チェック中...' : '今すぐ全件チェック'}
          </button>
        </div>
      </div>

      {error && <div className="health-check-error">{error}</div>}

      <div className="health-summary-grid">
        <div className="health-summary-card total">
          <div className="label">合計</div>
          <div className="value">{summary.total}</div>
        </div>
        <div className="health-summary-card healthy">
          <div className="label">正常</div>
          <div className="value">{summary.healthy}</div>
        </div>
        <div className="health-summary-card degraded">
          <div className="label">警告</div>
          <div className="value">{summary.degraded}</div>
        </div>
        <div className="health-summary-card down">
          <div className="label">異常</div>
          <div className="value">{summary.down}</div>
        </div>
        <div className="health-summary-card unconfigured">
          <div className="label">未設定</div>
          <div className="value">{summary.unconfigured}</div>
        </div>
        {(summary.pending ?? 0) > 0 && (
          <div className="health-summary-card pending">
            <div className="label">未実行</div>
            <div className="value">{summary.pending}</div>
          </div>
        )}
      </div>

      <div className="health-toolbar list-toolbar">
        <FilterGroup label="種別">
          <FilterButton active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')}>すべて</FilterButton>
          <FilterButton active={categoryFilter === 'platform'} onClick={() => setCategoryFilter('platform')}>プラットフォーム</FilterButton>
          <FilterButton active={categoryFilter === 'product'} onClick={() => setCategoryFilter('product')}>プロダクト</FilterButton>
        </FilterGroup>
        <FilterGroup label="公開">
          <FilterButton active={visibilityFilter === 'all'} onClick={() => setVisibilityFilter('all')}>すべて</FilterButton>
          <FilterButton active={visibilityFilter === 'public'} onClick={() => setVisibilityFilter('public')}>公開のみ</FilterButton>
          <FilterButton active={visibilityFilter === 'private'} onClick={() => setVisibilityFilter('private')}>非公開のみ</FilterButton>
        </FilterGroup>
        <FilterGroup label="環境">
          <FilterButton active={envFilter === 'all'} onClick={() => setEnvFilter('all')}>すべて</FilterButton>
          <FilterButton active={envFilter === 'prod'} onClick={() => setEnvFilter('prod')}>本番</FilterButton>
          <FilterButton active={envFilter === 'dev'} onClick={() => setEnvFilter('dev')}>開発</FilterButton>
        </FilterGroup>
        <FilterGroup label="状態">
          <FilterButton active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>すべて</FilterButton>
          <FilterButton active={statusFilter === 'healthy'} onClick={() => setStatusFilter('healthy')}>正常</FilterButton>
          <FilterButton active={statusFilter === 'degraded'} onClick={() => setStatusFilter('degraded')}>警告</FilterButton>
          <FilterButton active={statusFilter === 'down'} onClick={() => setStatusFilter('down')}>異常</FilterButton>
          <FilterButton active={statusFilter === 'unconfigured'} onClick={() => setStatusFilter('unconfigured')}>未設定</FilterButton>
          <FilterButton active={statusFilter === 'pending'} onClick={() => setStatusFilter('pending')}>未実行</FilterButton>
        </FilterGroup>
        <div className="list-toolbar__sort health-toolbar__sort">
          <span className="list-toolbar__sort-label">並び替え</span>
          <select
            className="admin-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            aria-label="並び替え項目"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <SortOrderToggle
            order={sortOrder}
            onToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          />
        </div>
      </div>

      {filteredResults.length === 0 ? (
        <div className="health-check-empty">
          {report?.results?.length
            ? 'フィルター条件に一致する監視対象がありません'
            : '監視対象を読み込めませんでした。「今すぐ全件チェック」を実行するか、スケジューラ（L2: 15分 / L3: 30分）の初回実行をお待ちください。'}
        </div>
      ) : (
        <div className="health-check-list">
          <div className="health-check-list-header" aria-hidden="true">
            <span className="health-check-col-name">監視対象</span>
            <span className="health-check-col-env">環境</span>
            <span className="health-check-col-status">状態</span>
            <span className="health-check-col-metrics">応答</span>
            <span className="health-check-col-actions" />
          </div>
          {filteredResults.map((item) => {
            const isExpanded = expandedId === item.id;
            const isProbing = probingId === item.id;
            const isPrivateProduct = item.category === 'product' && item.isActive === false;

            return (
              <div
                key={item.id}
                className={`health-check-row status-${item.status}${isProbing ? ' is-probing' : ''}${isPrivateProduct ? ' is-private-product' : ''}`}
              >
                <div
                  className={`health-check-row-main${isExpanded ? ' is-expanded' : ''}`}
                  onClick={() => toggleRowExpanded(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleRowExpanded(item.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  aria-label={`${item.name} の詳細を${isExpanded ? '閉じる' : '表示'}`}
                >
                  <div className="health-check-col-name">
                    <div className="health-check-primary">
                      <span className="health-check-title">{item.name}</span>
                      {isPrivateProduct && (
                        <span className="health-visibility-badge">非公開</span>
                      )}
                      {item.isComingSoon && (
                        <span className="health-visibility-badge coming-soon">準備中</span>
                      )}
                    </div>
                    <div className="health-check-secondary">
                      <span className="health-type-badge">{CATEGORY_LABELS[item.category] || item.category}</span>
                      <span className="health-check-url" title={item.sourceUrl || undefined}>
                        {shortenUrl(item.sourceUrl)}
                      </span>
                    </div>
                  </div>
                  <div className="health-check-col-env">
                    <span className={`health-env-badge ${item.environment}`}>
                      {ENV_LABELS[item.environment] || item.environment}
                    </span>
                  </div>
                  <div className="health-check-col-status">
                    <span className={`health-status-badge ${item.status}`}>
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                  </div>
                  <div className="health-check-col-metrics">
                    <span className="health-metric-value">{formatLatency(item.latencyMs)}</span>
                    <span className="health-metric-sub">HTTP {formatHttpStatus(item.httpStatus)}</span>
                  </div>
                  <div
                    className="health-check-col-actions"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-icon"
                      onClick={() => handleProbeOne(item.id)}
                      disabled={!!probingId || runningFull}
                      title="再チェック"
                      aria-label={`${item.name} を再チェック`}
                    >
                      <IconRefresh />
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm btn-icon health-expand-btn${isExpanded ? ' is-expanded' : ''}`}
                      onClick={() => toggleRowExpanded(item.id)}
                      title={isExpanded ? '詳細を閉じる' : '詳細を表示'}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? '詳細を閉じる' : '詳細を表示'}
                    >
                      <IconChevronDown />
                    </button>
                  </div>
                </div>
                <div className={`health-check-detail-wrap${isExpanded ? ' is-open' : ''}`}>
                  <div className="health-check-detail-inner">
                    <HealthCheckDetail item={item} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminHealthCheck;
